/**
 * Species Configuration - Single Source of Truth
 *
 * This file contains ONLY the species catalog data that gets synced to the database.
 *
 * When adding a new species:
 * 1. Add an entry to the SUIKAMON_SPECIES array below with a NEW unique id (never reuse or change existing IDs - they are referenced in the database)
 * 2. Ensure stageLevels array length = maxStage (NOT maxStage + 1)
 * 3. Ensure stageLevels[0] = 0 (eggs can always hatch regardless of level - placeholder only)
 * 4. stageLevels[N] = level required to evolve FROM Stage N TO Stage N+1
 *    Example: stageLevels[1] = 5 means you need Level 5 to evolve from Stage 1 to Stage 2
 * 5. stageLevels[maxStage - 1] should always be 30 (final evolution at level 30)
 * 6. Backend will auto-sync to species_configs table on startup
 *
 * DISCOUNT FIELD EXPLAINED:
 * - The `discount` field is a PRICE MULTIPLIER, NOT a percentage off
 * - discount: 1 = NO discount (0% off) = FULL PRICE ✅
 * - discount: 0.7 = 30% off (pay 70% of price)
 * - discount: 0.5 = 50% off (pay 50% of price)
 * - discount: 0 = 100% off (FREE) ❌
 * - Formula: finalPrice = basePrice * discount
 * - To show full price with no discount, use: discount: 1
 *
 * Data Flow:
 * - Define species here (version controlled, code review in PRs)
 * - Backend syncs to database on startup (automatic via speciesSync.ts)
 * - Frontend imports directly for UI (no API call needed)
 * - Backend queries database for game logic (performance + joins)
 *
 * Database Schema:
 * All fields below sync to the species_configs table:
 * - id → species_id
 * - name → name
 * - element → element
 * - rarity → rarity
 * - tagline → tagline
 * - description → description
 * - available → available
 * - behavior.baseXpMin → base_xp_min
 * - behavior.baseXpMax → base_xp_max
 * - behavior.maxStage → max_stage
 * - behavior.stageLevels[1-7] → stage_1_level through stage_7_level
 */

import type {
  CombatSkill,
  SuikamonSpecies,
  Rarity,
} from './petModels.js';
import { COMBAT_ARCHETYPE, COMBAT_SPECIAL_EFFECT } from './petModels.js';
import { COMBAT_CONSTANTS } from '../constants/battleConstants.js';
import {
  DODGE_SKILL_EMOJI,
  defineCombatSkill,
  describeDodgeSkill,
} from './combatSkillPresentation.js';

/**
 * Stage level distribution. All species now have 6 stages (egg + 5 evolutions),
 * hatch at level 2, reach evolution 5 at level 25, and the final evolution
 * (6) at level 30 — fixed and identical for every species.
 * Array length = maxStage (stageLevels[N] = level to evolve FROM stage N TO stage N+1).
 *
 * Why 25/30 are fixed (not derived/spaced like the earlier stages): existing
 * pets that previously maxed out at a 4- or 5-stage final evolution (final
 * level 20) keep leveling past 20 via normal play even after hitting their
 * old cap. 25/30 must stay HIGHER than any level those pets are realistically
 * at today, so no existing pet's stored stage can ever already exceed what
 * its level qualifies for — that ordering (stage N requires more level than
 * stage N-1, with no pet "ahead" of its level) is what the Move contract's
 * evolution check relies on. Never lower these without auditing live pet
 * levels first.
 *
 * Earlier levels skip 3 and multiples of 3 (3, 6, 9, 12, 15, 18, 21, 24) —
 * those are reserved for other progression milestones, not evolutions.
 *
 * - maxStage 6: [2, 8, 14, 25, 30] - intervals: 6, 6, 11, 5
 */
const STAGE_LEVEL_DISTRIBUTION: Record<number, number[]> = {
  6: [0, 2, 8, 14, 25, 30],
} as const;

/**
 * Rarity order for sorting (Common first → Legendary last)
 * Used for species selection UI to show accessible species first.
 */
const RARITY_SORT_ORDER: Record<Rarity, number> = {
  Common: 0,
  Rare: 1,
  Epic: 2,
  Legendary: 3,
  Unique: 4,
};

/**
 * **The Recover skill, and the base heal ladder it is built from.**
 *
 * Every species passes its own base heal here; the ladder is 29 / 30 / 33 / 38 / 39, tracking the
 * same tank-to-glass-cannon ordering as `baseHp`. `calculateScaledRecoverHeal` adds the level
 * term on top, and the printed description is derived from the number rather than written beside
 * it, so a re-tune can never leave a species advertising a heal it does not deal.
 *
 * **The ladder was raised by half in one step** (19/20/22/25/26 → 29/30/33/38/39, rounded up on a
 * half). The reason is `PRESSURE_PER_ROUND`, not the early fight: a heal is a flat number and
 * pressure multiplies every blow for the rest of the fight, so from round 4 on the two curves
 * diverge and Recover's fourth-move status decays to nothing by the time a fight is actually in
 * danger. The pressure step came down a fifth in the same change so the heal is not simply
 * outrun again three rounds later — the two are one adjustment and were measured as one. Tuning
 * log Row 53.
 */
function createRecoverSkill(heal: number): CombatSkill {
  return {
    name: "Recover",
    emoji: "❤️",
    damage: 0,
    heal,
    special: COMBAT_SPECIAL_EFFECT.None,
    description: `Heals ${heal} HP.`,
  };
}

type DodgeSkillOptions = {
  special: CombatSkill["special"];
  specialValue?: number;
  specialChance?: number;
};

function createDodgeSkill(options: DodgeSkillOptions): CombatSkill {
  const dodgeSkill: CombatSkill = {
    name: "Dodge",
    emoji: DODGE_SKILL_EMOJI,
    damage: 0,
    heal: 0,
    description: "",
    ...options,
  };

  return {
    ...dodgeSkill,
    description: describeDodgeSkill(dodgeSkill),
  };
}

/**
 * Sort species by rarity (Common first → Legendary last), then by name alphabetically.
 * @param species - Array of species to sort
 * @returns Sorted array (new array, does not mutate original)
 */
export function sortSpeciesByRarity(species: SuikamonSpecies[]): SuikamonSpecies[] {
  return [...species].sort((a, b) => {
    const rarityDiff = RARITY_SORT_ORDER[a.rarity] - RARITY_SORT_ORDER[b.rarity];
    if (rarityDiff !== 0) return rarityDiff;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Complete Species Catalog
 *
 * Add new species here - they will automatically sync to database on backend startup.
 * Frontend and backend both import from this single source of truth.
 */
export const SUIKAMON_SPECIES: SuikamonSpecies[] = [
  {
    id: 0,
    name: "Bireon",
    element: "Nature",
    rarity: "Common" as Rarity,
    tagline: "Tiny bird, endless kindness.",
    description: "🌱 Kindhearted bird that sings at sunrise. Shares seeds with friends and makes flowers bloom.",
    avatarEmoji: "🌱",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.DoT,
      baseHp: 57,
      attack: defineCombatSkill("attack", { name: "Feather Dart", emoji: "🪶", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(18),
      magic: defineCombatSkill("magic", { name: "Sky Talons", emoji: "🦅", damage: 16, heal: 0, special: "dot", specialValue: 28, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitDot, value: 13 } }),
      dodge: createDodgeSkill({ special: "dot", specialValue: 4, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 1,
    name: "Aqua",
    element: "Water",
    rarity: "Rare" as Rarity,
    tagline: "Graceful waves, cool spirit.",
    description: "💧 Graceful swimmer that glides through crystal waters, leaving sparkles in its wake. Brings calm wherever it goes.",
    avatarEmoji: "💧",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Stun,
      baseHp: 48,
      attack: defineCombatSkill("attack", { name: "Tide Slash", emoji: "🔱", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Water Lash", emoji: "💧", damage: 12, heal: 0, special: "stun", specialChance: 0.5, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitStun, value: 17 } }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 2,
    name: "Zaricat",
    element: "Thunder",
    rarity: "Rare" as Rarity,
    tagline: "Lightning that never sleeps.",
    description: "⚡ Storm spirit that awakens with thunder. Its electric laugh echoes across the sky.",
    avatarEmoji: "⚡",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Stun,
      baseHp: 51,
      attack: defineCombatSkill("attack", { name: "Lightning Claw", emoji: "🌩️", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Static Lock", emoji: "⚡", damage: 11, heal: 0, special: "stun", specialChance: 0.5, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitStun, value: 17 } }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 3,
    name: "Hotpaw",
    element: "Fire",
    rarity: "Rare" as Rarity,
    tagline: "Born from molten laughter.",
    description: "🔥 Born from volcanic hearts. Its paws embers light the way for travelers at night.",
    avatarEmoji: "🔥",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.DoT,
      baseHp: 60,
      attack: defineCombatSkill("attack", { name: "Ember Claw", emoji: "☄️", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(18),
      magic: defineCombatSkill("magic", { name: "Flame Burst", emoji: "🔥", damage: 13, heal: 0, special: "dot", specialValue: 22, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitDot, value: 13 } }),
      dodge: createDodgeSkill({ special: "dot", specialValue: 4, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 4,
    name: "Yeti",
    element: "Water",
    rarity: "Epic" as Rarity,
    tagline: "Silent strength beneath mountains.",
    description: "❄️ Ancient Yeti. Its footsteps make flowers bloom and rivers shift their course.",
    avatarEmoji: "❄️",
    available: true,
    behavior: {
      baseXpMin: 15,
      baseXpMax: 60,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 29.99,
    breedingReservedPercentage: 40,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Shield,
      baseHp: 61,
      attack: defineCombatSkill("attack", { name: "Frost Punch", emoji: "👊", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Snowball", emoji: "❄️", damage: 17, heal: 0, special: "shield", specialValue: 6, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitShield, value: COMBAT_CONSTANTS.SHIELD_CASH_OUT_CAP } }),
      dodge: createDodgeSkill({ special: "shield", specialValue: 5, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 5,
    name: "Toro",
    element: "Thunder",
    rarity: "Legendary" as Rarity,
    tagline: "A legendary bull with a heart of gold.",
    description: "🐂 A powerful bull that embodies strength and kindness.",
    avatarEmoji: "🐂",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 99.99,
    breedingReservedPercentage: 60,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Execute,
      baseHp: 56,
      attack: defineCombatSkill("attack", { name: "Horn Strike", emoji: "🐃", damage: 18, heal: 0, special: "execute", specialValue: 13 }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Wild Charge", emoji: "🐂", damage: 21, heal: 0, special: "execute", specialValue: 16, specialChance: 1 }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 6,
    name: "Kobu",
    element: "Nature",
    rarity: "Common" as Rarity,
    tagline: "Forest's tiny guardian.",
    description: "🍃 A tiny forest spirit that watches over woodland creatures. Its gentle presence brings peace to the forest floor.",
    avatarEmoji: "🍃",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.DoT,
      baseHp: 59,
      attack: defineCombatSkill("attack", { name: "Leaf Blade", emoji: "🗡️", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(18),
      magic: defineCombatSkill("magic", { name: "Savage Bite", emoji: "🍃", damage: 12, heal: 0, special: "dot", specialValue: 22, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitDot, value: 13 } }),
      dodge: createDodgeSkill({ special: "dot", specialValue: 5, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 7,
    name: "Pinky",
    element: "Water",
    rarity: "Epic" as Rarity,
    tagline: "A little fairy princess.",
    description: "🌷 A magical pink princess with fairy godmother powers. Her sparkles bring wishes to life and her gentle heart guides lost souls home.",
    avatarEmoji: "🌷",
    available: true,
    behavior: {
      baseXpMin: 15,
      baseXpMax: 60,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 29.99,
    breedingReservedPercentage: 40,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Shield,
      baseHp: 59,
      attack: defineCombatSkill("attack", { name: "Petal Strike", emoji: "🌸", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Wishing Bubble", emoji: "🫧", damage: 19, heal: 5, special: "shield", specialValue: 14, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitShield, value: COMBAT_CONSTANTS.SHIELD_CASH_OUT_CAP } }),
      dodge: createDodgeSkill({ special: "shield", specialValue: 5, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 8,
    name: "Pefareon",
    element: "Nature",
    rarity: "Common" as Rarity,
    tagline: "A sweet little vampire.",
    description: "🦇 A gentle vampire creature with a heart of gold. Despite its fangs, it only nibbles on moonberries and spreads kindness through the night.",
    avatarEmoji: "🦇",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.Wound,
      baseHp: 54,
      attack: defineCombatSkill("attack", { name: "Fang Strike", emoji: "🦷", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Blood Drain", emoji: "🦇", damage: 21, heal: 0, special: "wound", specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitWound, value: 15 } }),
      dodge: createDodgeSkill({ special: "wound", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 9,
    name: "Talion",
    element: "Fire",
    rarity: "Common" as Rarity,
    tagline: "A cunning little bandit.",
    description: "🦊 A mischievous red fox with a heart of adventure. Steals only smiles and leaves behind trails of laughter in the moonlit woods.",
    avatarEmoji: "🦊",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.Execute,
      baseHp: 58,
      attack: defineCombatSkill("attack", { name: "Ambush Slash", emoji: "🗡️", damage: 19, heal: 0, special: "execute", specialValue: 13 }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Immolate", emoji: "🔥", damage: 20, heal: 0, special: "execute", specialValue: 17, specialChance: 1 }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 10,
    name: "Suimon",
    element: "Water",
    rarity: "Epic" as Rarity,
    tagline: "A mystical sea spirit summoned by the Suimon community.",
    description: "🌊 Invoked from the deep tides by the @Suimon_at_Sui community, this ancient sea spirit channels powerful water magic.",
    avatarEmoji: "🌊",
    available: true,
    behavior: {
      baseXpMin: 15,
      baseXpMax: 60,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 29.99,
    breedingReservedPercentage: 40,
    discount: 0.9,
    combat: {
      archetype: COMBAT_ARCHETYPE.Wound,
      baseHp: 50,
      attack: defineCombatSkill("attack", { name: "Crushing Wave", emoji: "💥", damage: 16, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Suiwave", emoji: "🌊", damage: 24, heal: 0, special: "wound", specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitWound, value: 14 } }),
      dodge: createDodgeSkill({ special: "wound", specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 11,
    name: "Voltaryx",
    element: "Thunder",
    rarity: "Legendary" as Rarity,
    tagline: "Storm incarnate, wings of lightning.",
    description: "🐉 An ancient thunder dragon born from the heart of a superstorm. Brought to life by artist Feder and the Mash-It community.",
    avatarEmoji: "🐉",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 99.99,
    breedingReservedPercentage: 0,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Stun,
      baseHp: 49,
      attack: defineCombatSkill("attack", { name: "Storm Claw", emoji: "🌪️", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Thunder Strike", emoji: "🐉", damage: 12, heal: 0, special: "stun", specialChance: 0.5, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitStun, value: 18 } }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 12,
    name: "Drago",
    element: "Fire",
    rarity: "Epic" as Rarity,
    tagline: "A legendary fire dragon with an unbreakable will.",
    description: "🔥 A fierce ruler of the skies that embodies power, pride, and burning intensity.",
    avatarEmoji: "🔥",
    available: true,
    behavior: {
      baseXpMin: 15,
      baseXpMax: 60,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 29.99,
    breedingReservedPercentage: 40,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.DoT,
      baseHp: 59,
      attack: defineCombatSkill("attack", { name: "Searing Claw", emoji: "🔥", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(18),
      magic: defineCombatSkill("magic", { name: "Inferno Breath", emoji: "🐉", damage: 13, heal: 0, special: "dot", specialValue: 22, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitDot, value: 13 } }),
      dodge: createDodgeSkill({ special: "dot", specialValue: 4, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 13,
    name: "Fernail",
    element: "Water",
    rarity: "Rare" as Rarity,
    tagline: "Slow steps, deep courage.",
    description: "🐌 A tiny water snail with a shell that shimmers like the ocean floor. Its slow, steady presence calms restless tides and brings peace to troubled waters.",
    avatarEmoji: "🐌",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Shield,
      baseHp: 56,
      attack: defineCombatSkill("attack", { name: "Shell Bump", emoji: "🐚", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Shell Slam", emoji: "🐌", damage: 19, heal: 0, special: "shield", specialValue: 9, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitShield, value: COMBAT_CONSTANTS.SHIELD_CASH_OUT_CAP } }),
      dodge: createDodgeSkill({ special: "shield", specialValue: 6, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 14,
    name: "Sumiya",
    element: "Nature",
    rarity: "Common" as Rarity,
    tagline: "Tiny wizard, pink mischief.",
    description: "🌸 A cute pink rabbit wizard that casts spells from a handmade wand. Hops through enchanted meadows leaving glowing flower petals in its wake.",
    avatarEmoji: "🌸",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.Wound,
      baseHp: 53,
      attack: defineCombatSkill("attack", { name: "Wand Strike", emoji: "🪄", damage: 18, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Pink Hex", emoji: "🌸", damage: 23, heal: 0, special: "wound", specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitWound, value: 14 } }),
      dodge: createDodgeSkill({ special: "wound", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3],
      preciseAttack: false
    }
  },
  {
    id: 15,
    name: "Trixon",
    element: "Water",
    rarity: "Epic" as Rarity,
    tagline: "Elegant blue dragon of the Japanese tides.",
    description: "🐉 A beautiful and elegant blue dragon from Japan. Its serpentine grace commands the ocean currents and stuns all who behold it.",
    avatarEmoji: "🐉",
    available: true,
    behavior: {
      baseXpMin: 15,
      baseXpMax: 60,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 29.99,
    breedingReservedPercentage: 40,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Stun,
      baseHp: 47,
      attack: defineCombatSkill("attack", { name: "Tide Claw", emoji: "🌊", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(20),
      magic: defineCombatSkill("magic", { name: "Hydro Storm", emoji: "🐉", damage: 10, heal: 0, special: "stun", specialChance: 0.5, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitStun, value: 14 } }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 16,
    name: "Pikoto",
    element: "Nature",
    rarity: "Common" as Rarity,
    tagline: "Grumpy farm bird, all about gains.",
    description: "🐔 A grumpy farm bird obsessed with its muscles. Skips leg day for nobody and pecks anyone who interrupts its workout.",
    avatarEmoji: "🐔",
    available: true,
    behavior: {
      baseXpMin: 5,
      baseXpMax: 25,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 4.95,
    breedingReservedPercentage: 0,
    discount: 0.2,
    combat: {
      archetype: COMBAT_ARCHETYPE.Execute,
      baseHp: 62,
      attack: defineCombatSkill("attack", { name: "Flex Jab", emoji: "💪", damage: 16, heal: 0, special: "execute", specialValue: 13 }),
      recover: createRecoverSkill(18),
      magic: defineCombatSkill("magic", { name: "Rage Cluck", emoji: "🐔", damage: 16, heal: 0, special: "execute", specialValue: 16, specialChance: 1 }),
      dodge: createDodgeSkill({ special: "stun", specialChance: 0.5 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 17,
    name: "Ray",
    element: "Nature",
    rarity: "Rare" as Rarity,
    tagline: "Gentle paws, forest soul.",
    description: "🐻 A quiet woodland bear who roams the deep forest. Friend to every cub, fierce to anyone who threatens the grove.",
    avatarEmoji: "🐻",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.DoT,
      baseHp: 53,
      attack: defineCombatSkill("attack", { name: "Claw Swipe", emoji: "🐾", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(19),
      magic: defineCombatSkill("magic", { name: "Bramble Bite", emoji: "🌿", damage: 17, heal: 0, special: "dot", specialValue: 20, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitDot, value: 13 } }),
      dodge: createDodgeSkill({ special: "dot", specialValue: 3, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
  {
    id: 18,
    name: "Gorly",
    element: "Nature",
    rarity: "Rare" as Rarity,
    tagline: "Lazy, hungry, unstoppable.",
    description: "👹 A big blue jungle monster who'd rather nap than fight — until you touch his snacks. Then the whole forest trembles.",
    avatarEmoji: "👹",
    available: true,
    behavior: {
      baseXpMin: 10,
      baseXpMax: 40,
      maxStage: 6,
      stageLevels: STAGE_LEVEL_DISTRIBUTION[6],
    },
    adoptionPrice: 9.99,
    breedingReservedPercentage: 50,
    discount: 1,
    combat: {
      archetype: COMBAT_ARCHETYPE.Shield,
      baseHp: 57,
      attack: defineCombatSkill("attack", { name: "Snack Smash", emoji: "🍖", damage: 14, heal: 0, special: COMBAT_SPECIAL_EFFECT.None }),
      recover: createRecoverSkill(24),
      magic: defineCombatSkill("magic", { name: "Food Coma", emoji: "🍯", damage: 19, heal: 0, special: "shield", specialValue: 7, specialChance: 1, punish: { effect: COMBAT_SPECIAL_EFFECT.ExploitShield, value: COMBAT_CONSTANTS.SHIELD_CASH_OUT_CAP } }),
      dodge: createDodgeSkill({ special: "shield", specialValue: 6, specialChance: 1 }),
      recoverUsesByStage: [0, 2, 3]
    }
  },
];

/**
 * Pre-sorted species list by rarity (Common → Legendary).
 * Use this for species selection UIs.
 */
export const SUIKAMON_SPECIES_BY_RARITY = sortSpeciesByRarity(SUIKAMON_SPECIES);
